import { useMemo } from '@integrations/react'
import { useNavigate } from '@integrations/routing'
import { useAppDispatch, useAppSelector } from '@store'
import {
  api,
  CustomerRoles,
  paths,
  selectCurrentUserRole,
  selectMerchant,
  useGetMerchantsForSwitchQuery,
  useSwitchMerchantMutation
} from 'mmfintech-backend-api'
import { tr } from 'mmfintech-commons'

function subAccountLabel(merchantId?: number) {
  return tr('FRONTEND.HEADER.SUB_ACCOUNT', 'Sub account {{ID}}', { ID: merchantId })
}

export function useMerchantSwitch() {
  const { data: merchant } = useAppSelector(selectMerchant)
  const customerRole = useAppSelector(selectCurrentUserRole)
  const { data: merchants } = useGetMerchantsForSwitchQuery(null)

  const [switchMerchant, { isLoading }] = useSwitchMerchantMutation()

  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  // Prospect (not yet onboarded) sub-accounts come back without a `name`, so each entry gets a label to render.
  // BE gap (PLAT-17169): the API should supply a title and a main/sub marker; this label is only a workaround.
  // `currentMerchantName` below keeps using the raw name so the header pill can still fall back to the user's email.
  const merchantList = useMemo(
    () =>
      (Array.isArray(merchants) ? merchants : []).map((v) => ({
        ...v,
        displayName: v.name || subAccountLabel(v.merchantId)
      })),
    [merchants]
  )
  const hasSubAccounts = merchantList.length > 1
  const canSwitch = customerRole !== CustomerRoles.STAFF && hasSubAccounts
  const currentMerchantId = merchant?.merchantId
  const currentMerchantName = merchantList.find((v) => v.merchantId === currentMerchantId)?.name || merchant?.name
  // Only users with sub-accounts get the generic label for an unnamed current merchant; a user with a single unnamed
  // (freshly registered) merchant keeps no label so the header falls back to their email.
  const currentMerchantLabel = currentMerchantName || (hasSubAccounts ? subAccountLabel(currentMerchantId) : undefined)

  async function selectMerchantById(merchantId: number) {
    if (!isLoading && currentMerchantId !== merchantId) {
      try {
        await switchMerchant({ merchantId }).unwrap()
        navigate(paths.dashboard())
        dispatch(api.util.resetApiState())
      } catch (_error) {}
    }
  }

  return { merchantList, canSwitch, currentMerchantId, currentMerchantName, currentMerchantLabel, selectMerchantById }
}
